import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Phone,
  MoreVertical,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  XCircle,
  RotateCcw,
  ExternalLink,
  Info,
  Loader2,
  Plus,
  X,
  Workflow,
  StopCircle
} from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { useAuthStore } from '@/stores/auth-store';
import { Button, useToast, Popover, PopoverTrigger, PopoverContent } from '@caruso/ui';
import { cn } from '@caruso/ui';
import { TransferDialog } from './transfer-dialog';
import { ClientQuickInfoDialog } from './client-quick-info-dialog';
import { chatService, ConversationTag } from '@/services/chat.service';
import { tagsService } from '@/services/tags.service';
import { flowsService, FlowExecution } from '@/services/flows.service';

// Função para formatar telefone
const formatPhone = (phone?: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');

  // Brazilian numbers with country code
  if (digits.startsWith('55') && digits.length >= 12) {
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    if (rest.length === 9) {
      return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
    } else if (rest.length === 8) {
      return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    }
  }

  // Brazilian without country code
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return phone;
};

const statusLabels: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Aberta', color: 'bg-yellow-500' },
  IN_PROGRESS: { label: 'Em Atendimento', color: 'bg-blue-500' },
  WAITING: { label: 'Aguardando', color: 'bg-orange-500' },
  CLOSED: { label: 'Fechada', color: 'bg-gray-500' },
};

export function ChatHeader() {
  const [showMenu, setShowMenu] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showQuickInfo, setShowQuickInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTagPopover, setShowTagPopover] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);

  const {
    selectedConversation,
    claimConversation,
    releaseConversation,
    closeConversation,
    reopenConversation,
    updateConversation
  } = useChatStore();

  // Buscar todas as tags disponiveis
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsService.getAll(),
  });

  // Mutation para adicionar tag
  const addTagMutation = useMutation({
    mutationFn: ({ conversationId, tagId }: { conversationId: string; tagId: string }) =>
      chatService.addTag(conversationId, tagId),
    onSuccess: (_, variables) => {
      const tag = allTags.find((t) => t.id === variables.tagId);
      if (tag && selectedConversation) {
        const newTags = [...(selectedConversation.tags || []), tag];
        updateConversation(selectedConversation.id, { tags: newTags });
      }
      toast({ title: 'Tag adicionada', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao adicionar tag', variant: 'destructive' });
    },
  });

  // Mutation para remover tag
  const removeTagMutation = useMutation({
    mutationFn: ({ conversationId, tagId }: { conversationId: string; tagId: string }) =>
      chatService.removeTag(conversationId, tagId),
    onSuccess: (_, variables) => {
      if (selectedConversation) {
        const newTags = (selectedConversation.tags || []).filter((t) => t.id !== variables.tagId);
        updateConversation(selectedConversation.id, { tags: newTags });
      }
      toast({ title: 'Tag removida', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover tag', variant: 'destructive' });
    },
  });

  // Buscar fluxos ativos do cliente
  const { data: activeFlowsData, refetch: refetchActiveFlows } = useQuery({
    queryKey: ['activeFlows', selectedConversation?.clientId],
    queryFn: () => flowsService.getExecutions({
      clientId: selectedConversation?.clientId,
      status: 'running',
    }),
    enabled: !!selectedConversation?.clientId,
  });

  const activeFlows = activeFlowsData?.data || [];

  // Mutation para cancelar fluxo
  const cancelFlowMutation = useMutation({
    mutationFn: (executionId: string) => flowsService.cancelExecution(executionId),
    onSuccess: () => {
      toast({ title: 'Fluxo cancelado', description: 'O fluxo foi interrompido com sucesso.', variant: 'success' });
      refetchActiveFlows();
    },
    onError: () => {
      toast({ title: 'Erro ao cancelar fluxo', variant: 'destructive' });
    },
  });

  if (!selectedConversation) return null;

  const isAssignedToMe = selectedConversation.assignedUserId === user?.id;
  const isClosed = selectedConversation.status === 'CLOSED';
  const statusInfo = statusLabels[selectedConversation.status];
  const conversationTags = selectedConversation.tags || [];
  const conversationTagIds = conversationTags.map((t) => t.id);
  const availableTags = allTags.filter((t) => !conversationTagIds.includes(t.id));

  const handleClaim = async () => {
    setIsLoading(true);
    try {
      await claimConversation(selectedConversation.id);
      toast({
        title: 'Conversa assumida',
        description: 'Voce agora esta atendendo esta conversa.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel assumir a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowMenu(false);
    }
  };

  const handleRelease = async () => {
    setIsLoading(true);
    try {
      await releaseConversation(selectedConversation.id);
      toast({
        title: 'Conversa liberada',
        description: 'A conversa foi liberada para outros atendentes.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel liberar a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowMenu(false);
    }
  };

  const handleClose = async () => {
    setIsLoading(true);
    try {
      await closeConversation(selectedConversation.id);
      toast({
        title: 'Conversa encerrada',
        description: 'A conversa foi encerrada com sucesso.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel encerrar a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowMenu(false);
    }
  };

  const handleReopen = async () => {
    setIsLoading(true);
    try {
      await reopenConversation(selectedConversation.id);
      toast({
        title: 'Conversa reaberta',
        description: 'A conversa foi reaberta com sucesso.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel reabrir a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowMenu(false);
    }
  };

  return (
    <>
      <div className="hidden md:flex items-center justify-between px-4 pt-5 pb-3 border-b border-brand-gold/10 bg-brand-ink/50">
        {/* Client Info */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center">
            <User className="w-5 h-5 text-brand-gold" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/app/clientes/${selectedConversation.clientId}`)}
                className="font-medium text-brand-ivory hover:text-brand-gold transition-colors"
              >
                {selectedConversation.client.name}
              </button>
              <span className={cn(
                'px-1.5 py-0.5 rounded text-xs font-medium text-white',
                statusInfo.color
              )}>
                {statusInfo.label}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-brand-ivory/50">
              <Phone className="w-3.5 h-3.5" />
              <span>{formatPhone(selectedConversation.whatsappPhone)}</span>
              {selectedConversation.assignedUser && (
                <>
                  <span className="text-brand-gold/30">|</span>
                  <span>Atendente: {selectedConversation.assignedUser.name}</span>
                </>
              )}
            </div>
            {/* Tags */}
            <div className="flex items-center gap-1 mt-1">
              {conversationTags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs group antialiased !font-normal text-brand-gold"
                  style={{
                    backgroundColor: tag.color ? `${tag.color}20` : 'rgba(209, 183, 138, 0.2)',
                    ...(tag.color && { color: tag.color }),
                  }}
                >
                  {tag.name}
                  <button
                    onClick={() => removeTagMutation.mutate({ conversationId: selectedConversation.id, tagId: tag.id })}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {/* Add Tag Button */}
              <Popover open={showTagPopover} onOpenChange={setShowTagPopover}>
                <PopoverTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-smoke/50 text-brand-ivory/50 hover:text-brand-gold hover:bg-brand-gold/10 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Tag
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2 bg-brand-ink border border-brand-gold/20" align="start">
                  <div className="space-y-1">
                    {availableTags.length === 0 ? (
                      <p className="text-xs text-brand-ivory/50 text-center py-2">
                        Nenhuma tag disponivel
                      </p>
                    ) : (
                      availableTags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => {
                            addTagMutation.mutate({ conversationId: selectedConversation.id, tagId: tag.id });
                            setShowTagPopover(false);
                          }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-brand-ivory hover:bg-brand-gold/10 transition-colors"
                        >
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: tag.color || '#D1B78A' }}
                          />
                          {tag.name}
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Active Flows */}
            {activeFlows.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Workflow className="w-3 h-3 text-blue-400" />
                <span className="text-xs text-blue-400 mr-1">Fluxos ativos:</span>
                {activeFlows.map((execution) => (
                  <span
                    key={execution.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs group bg-blue-500/20 text-blue-400"
                  >
                    {execution.flow?.name || 'Fluxo'}
                    <button
                      onClick={() => cancelFlowMutation.mutate(execution.id)}
                      disabled={cancelFlowMutation.isPending}
                      className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                      title="Parar fluxo"
                    >
                      {cancelFlowMutation.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <StopCircle className="w-3 h-3" />
                      )}
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Quick Actions */}
          {!isClosed && !isAssignedToMe && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClaim}
              disabled={isLoading}
              className="text-brand-gold border-brand-gold/30 hover:bg-brand-gold/10"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Assumir
            </Button>
          )}

          {/* Menu Button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMenu(!showMenu)}
              className="text-brand-ivory/70 hover:text-brand-gold"
            >
              <MoreVertical className="w-5 h-5" />
            </Button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-brand-ink border border-brand-gold/20 rounded-lg shadow-xl z-50 py-1">
                  {!isClosed && (
                    <>
                      {isAssignedToMe ? (
                        <button
                          onClick={handleRelease}
                          disabled={isLoading}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                        >
                          <UserMinus className="w-4 h-4" />
                          Liberar Conversa
                        </button>
                      ) : (
                        <button
                          onClick={handleClaim}
                          disabled={isLoading}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                        >
                          <UserPlus className="w-4 h-4" />
                          Assumir Conversa
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowTransferDialog(true);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                        Transferir
                      </button>

                      <div className="my-1 border-t border-brand-gold/10" />

                      <button
                        onClick={handleClose}
                        disabled={isLoading}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-400/10"
                      >
                        <XCircle className="w-4 h-4" />
                        Encerrar Conversa
                      </button>
                    </>
                  )}

                  {isClosed && (
                    <button
                      onClick={handleReopen}
                      disabled={isLoading}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reabrir Conversa
                    </button>
                  )}

                  <div className="my-1 border-t border-brand-gold/10" />

                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowQuickInfo(true);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                  >
                    <Info className="w-4 h-4" />
                    Info Rapida
                  </button>

                  <a
                    href={`/app/clientes/${selectedConversation.clientId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-ivory/70 hover:bg-brand-gold/10 hover:text-brand-gold"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver Cliente
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Transfer Dialog */}
      <TransferDialog
        isOpen={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        conversationId={selectedConversation.id}
      />

      {/* Quick Info Dialog */}
      <ClientQuickInfoDialog
        isOpen={showQuickInfo}
        onClose={() => setShowQuickInfo(false)}
        clientId={selectedConversation.clientId}
      />
    </>
  );
}
